import { useState } from 'react';
import { Spinner } from '@chakra-ui/react';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useParams } from 'react-router-dom';
import CandidateAttachments from '@/components/CandidateAttachments';
import CandidateEducation from '@/components/CandidateEducation';
import { EducationType } from '@/components/CandidateEducationItem/CandidateEducationItem.types';
import CandidateExpertise from '@/components/CandidateExpertise';
import CandidateOverviewForm from '@/components/CandidateOverviewForm';
import CandidateProfessionalExperience from '@/components/CandidateProfessionalExperience';
import CandidateProjects from '@/components/CandidateProjects';
import Menu from '@/components/Menu';
import SectionBox from '@/components/SectionBox';
import TextEditor from '@/components/TextEditor';
import { WorkExperienceType } from '@/components/WorkExperience.types';
import { useToastContext } from '@/contexts/toastContext';
import { useAutofillFromResume } from '@/hooks/useAutofillFromResume';
import { EditableCandidate } from '@/services/candidates';
import { DropdownWithLabelOptionType } from '@/trio-ui/Dropdown/Dropdown.types';
import { ExpertiseType, Question } from '@/types';
import * as Styles from './CandidateInfo.styles';

type CandidateInfoComponentProps = {
  refetchCandidateData: () => void;
  onSaveText: (value: string) => void;
  workExperiences: WorkExperienceType[];
  expertises: ExpertiseType[];
  candidateEducation: EducationType[];
  candidate: EditableCandidate;
  questions: Question[];
  techOptions: DropdownWithLabelOptionType[];
  updatedField: string | undefined;
  onSubmit: (candidate: EditableCandidate) => Promise<void>;
  setUpdatedField: (field: string) => void;
  onScrollTo: (elementId: string) => void;
  onSubmitFromResume: (candidate: EditableCandidate) => Promise<void>;
};

const CandidateInfoComponent = function ({
  refetchCandidateData,
  workExperiences,
  expertises,
  candidateEducation,
  questions,
  candidate,
  updatedField,
  techOptions,
  onSubmit,
  setUpdatedField,
  onScrollTo,
  onSaveText,
  onSubmitFromResume,
}: CandidateInfoComponentProps): JSX.Element {
  const { id: candidateId } = useParams();
  const toast = useToastContext();
  const { extractFromResume, isLoading } = useAutofillFromResume(candidateId as string);

  const handleAutofillSummary = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();

    try {
      const summary = await extractFromResume('summary');
      onSaveText(summary);
      toast.showSuccessFeedback('Summary autofilled successfully');
    } catch (error) {
      toast.showErrorFeedback(
        'Failed to autofill summary from resume. Please check if the resume is a valid PDF file.'
      );
    }
  };

  return (
    <div data-testid="candidate-info-page">
      <Styles.PageGrid>
        <Styles.Sidebar
          testId="candidate-sidebar"
          sidebarMenus={[
            { label: 'Summary', id: 'sidebar-summary', onClick: () => onScrollTo('summary') },
            {
              label: 'Basic Info',
              id: 'sidebar-basic-info',
              onClick: () => onScrollTo('basic-info'),
            },
            {
              label: 'Professional Experience',
              id: 'sidebar-professional-experience',
              onClick: () => onScrollTo('professional-experience'),
            },
            {
              label: 'Education',
              id: 'sidebar-education',
              onClick: () => onScrollTo('education'),
            },
            { label: 'Projects', id: 'sidebar-projects', onClick: () => onScrollTo('projects') },
            {
              label: 'Attachments',
              id: 'sidebar-attachment',
              onClick: () => onScrollTo('attachments'),
            },
          ]}
        />

        <Styles.Content>
          <SectionBox
            title="Summary"
            leftElement={isLoading ? <Spinner size="xs" /> : undefined}
            rightElement={
              <Menu
                icon={MoreHorizIcon}
                menuId="autofill-summary-from-resume-button"
                menuItems={[
                  {
                    label: 'Autofill from Resume',
                    menuItemIcon: AutoFixHigh,
                    callback: handleAutofillSummary,
                  },
                ]}
              />
            }
          >
            <TextEditor
              onSaveText={onSaveText}
              value={candidate.summary ?? ''}
              data-testid="candidate-summary"
            />
          </SectionBox>

          <CandidateOverviewForm
            candidate={candidate}
            questions={questions}
            onSubmit={onSubmit}
            setUpdatedField={setUpdatedField}
            updatedField={updatedField}
            onSubmitFromResume={onSubmitFromResume}
          />

          <CandidateExpertise
            defaultExpertises={expertises}
            refetchCandidateData={refetchCandidateData}
          />

          <CandidateProfessionalExperience
            defaultWorkExperiences={workExperiences}
            techOptions={techOptions}
          />

          <CandidateEducation defaultEducationItems={candidateEducation} />

          <CandidateProjects />

          <CandidateAttachments />
        </Styles.Content>
      </Styles.PageGrid>
    </div>
  );
};

export default CandidateInfoComponent;
